import { TrashIcon } from "@heroicons/react/24/solid"
import * as DateFns from "date-fns"
import hotkeys from "hotkeys-js"
import React, { useContext, useEffect, useState } from "react"

import * as Commands from "../../../model/commands"
import { FleetingIdea } from "../../../model/types"
import { plainTextFromHTML } from "../../../services/text"
import { FleetingIdeaBox } from "../../components/idea_box"
import { HelpInline } from "../../components/help_bubble"
import { useNana } from "../../hooks/useNana"
import { DeviceContext, KEYS } from "../../providers/device_context"
import CaptureEditor from "./capture_page/capture_editor"

export default function CapturePage() {
  const { model, commander, hub, initialLoadComplete } = useNana()

  const { platform } = useContext(DeviceContext)

  const [fleetingIdeas, setFleetingIdeas] = useState<FleetingIdea[]>([])

  useEffect(() => {
    hotkeys(KEYS.undo[platform], { scope: "capture" }, function (event, handler) {
      event.preventDefault()
      commander.undo()
    })

    hotkeys(KEYS.redo[platform], { scope: "capture" }, function (event, handler) {
      event.preventDefault()
      commander.redo()
    })

    return () => {
      hotkeys.unbind(KEYS.undo[platform], "capture")
      hotkeys.unbind(KEYS.redo[platform], "capture")
    }
  }, [])

  hotkeys.setScope("capture")

  const updateIdeaList = () => {
    setFleetingIdeas(
      model
        .getFleetingIdeasWithNoSource()
        .sort((a, b) => DateFns.compareDesc(a.createdAt, b.createdAt))
    )
  }

  useEffect(() => {
    const keys = [
      hub.on("model:fleeting-idea-created", updateIdeaList),
      hub.on("model:fleeting-idea-deleted", updateIdeaList),
    ]
    return () => {
      keys.forEach((k) => hub.remove(k))
    }
  }, [])

  useEffect(() => {
    updateIdeaList()
  }, [initialLoadComplete])

  const deleteFleetingIdea = (idea: FleetingIdea) => {
    commander.exec(new Commands.DeleteFleetingIdeaCommand({ id: idea.id }))
  }

  return (
    <div className="flex min-h-0 w-full flex-auto flex-col gap-2 pt-16 dark:bg-gray-950">
      <div className="min-h-0 w-full flex flex-col justify-start divide-y divide-gray-200 dark:divide-gray-700  bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="p-4 md:p-8 bg-gray-50 dark:bg-gray-900">
          <CaptureEditor />
        </div>

        <div className="p-4 flex flex-col min-h-0">
          <FleetingIdeasList
            fleetingIdeas={fleetingIdeas}
            deleteFleetingIdea={deleteFleetingIdea}
          />
        </div>
      </div>

      {fleetingIdeas.length == 0 && (
        <HelpInline title="">
          <HelpCapturePage />
        </HelpInline>
      )}
    </div>
  )
}

const FleetingIdeasList = ({
  fleetingIdeas,
  deleteFleetingIdea,
}: {
  fleetingIdeas: FleetingIdea[]
  deleteFleetingIdea: (idea: FleetingIdea) => void
}) => {
  return (
    <div className="flex-grow p-2 w-full flex flex-col items-start gap-1 overflow-y-scroll">
      {fleetingIdeas.length === 0 && (
        <div className="text-gray-400 text-sm">No ideas captured yet.</div>
      )}
      {fleetingIdeas.map((idea, i) => (
        <div
          key={"fleeting-" + idea.id}
          className="flex w-full flex-row items-start justify-between gap-2 p-1 hover:bg-sky-50 dark:hover:bg-sky-900"
        >
          <div className="flex flex-row items-start gap-2">
            <FleetingIdeaBox />
            <div className="text-sm font-medium" style={{ wordBreak: "break-word" }}>
              {plainTextFromHTML(idea.content)}
            </div>
          </div>
          <div
            onClick={() => deleteFleetingIdea(idea)}
            className="cursor-pointer text-sm text-gray-300 hover:bg-sky-100 dark:hover:bg-sky-700 p-2 rounded"
          >
            <TrashIcon className="h-4 w-4 text-sm" />
          </div>
        </div>
      ))}
    </div>
  )
}

const HelpCapturePage = () => {
  return (
    <>
      <p>
        Use this page to capture ideas rapidly. They should be short - just something to jog your
        memory.
      </p>
      <p>
        Later, take them from here and place them into the Mosaic, and rewrite them fully into
        permanent notes.
      </p>
    </>
  )
}
